import axios from 'axios';
import logger from './logger';
import envConfig from '../config/config';

const {
  apps: { ehrIntegrationService: ehrIntegrationServiceURL, providerAccount: providerAccountURL },
} = envConfig;

const ehrIntegrationService = axios.create({
  baseURL: ehrIntegrationServiceURL,
});
const providerAccountService = axios.create({
  baseURL: providerAccountURL,
});
const EHR_LAUNCH_ISSUER = 'EHR_HUB_LAUNCH_ISSUER';
const EHR_CLIENT_ID = 'EHR_CLIENT_ID';
const EHR_HIDE_NAV_BAR = 'EHR_HIDE_NAV_BAR';
const EHR_HIDE_TOP_SEARCH = 'EHR_HIDE_TOP_SEARCH';
const EHR_HIDE_PATIENT_BANNER = 'EHR_HIDE_PATIENT_BANNER';
const EHR_HIDE_PATIENT_SIMILARITY = 'EHR_HIDE_PATIENT_SIMILARITY';
const EHR_HIDE_PATIENT_FILE_UPLOAD = 'EHR_HIDE_PATIENT_FILE_UPLOAD';
const EHR_HIDE_PATIENT_REPORT_ACTIONS = 'EHR_HIDE_PATIENT_REPORT_ACTIONS';
const EHR_HIDE_DATA_DOWNLOAD = 'EHR_HIDE_DATA_DOWNLOAD';
const EHR_PROVIDER_EMAIL = 'EHR_PROVIDER_EMAIL';

export function isValidEHRQueryParams(qs) {
  const validLaunchParameters = !!qs.iss && !!qs.launch;
  const validCodeExchangeParameters = !!qs.code;
  return validLaunchParameters || validCodeExchangeParameters;
}

export async function handleEhrLaunch(launch, iss) {
  try {
    sessionStorage.setItem(EHR_LAUNCH_ISSUER, iss);
    const resp = await ehrIntegrationService.post('/launch', {
      launch,
      iss,
    });
    logger.info(resp.data);
    if (resp?.data?.appMetaData?.clientId) {
      logger.info('Setting clientId in session storage', { clientId: resp.data.appMetaData.clientId });
      sessionStorage.setItem(EHR_CLIENT_ID, resp.data.appMetaData.clientId);
    }

    if (resp?.data?.redirectURL) {
      logger.info('Redirecting from EHR Launch to authorization page', { redirectURL: resp.data.redirectURL });
      window.location = resp.data.redirectURL;
    }
  } catch (error) {
    logger.error('Error while handling EHR Launch', { error: error.message });
  }
}

export async function handleEhrCodeExchange(code) {
  try {
    const iss = sessionStorage.getItem(EHR_LAUNCH_ISSUER);
    logger.debug('ISS from session storage', { iss: iss });

    const clientId = sessionStorage.getItem(EHR_CLIENT_ID);
    logger.debug('Client ID from session storage', { clientId: clientId });

    const resp = await ehrIntegrationService.post('/code-exchange', {
      code,
      issuer: iss,
      clientId,
    });

    if (resp?.data?.displayOptions) {
      logger.info('Setting diplay options');
      setEhrDisplayOptions(resp.data.displayOptions);
    }

    if (resp?.data?.providerEmail) {
      logger.info('Handling post login actions for EHR launch', { email: resp.data.providerEmail });
      sessionStorage.setItem(EHR_PROVIDER_EMAIL, resp.data.providerEmail);

      // await PostLoginActions(resp.data.providerEmail);
    }

    if (resp?.data?.redirectURL) {
      logger.info('Redirecting from code exchange to Hub page', { redirectURL: resp.data.redirectURL });
      sessionStorage.removeItem(EHR_LAUNCH_ISSUER);
      return resp.data.redirectURL;
    }

    return;
  } catch (error) {
    logger.error('Error while handling EHR code exchange', { error: error.message });
  }
}

export function triggerIDPDiscovery() {
  const providerEmail = sessionStorage.getItem(EHR_PROVIDER_EMAIL);
  sessionStorage.removeItem(EHR_PROVIDER_EMAIL);

  if (!providerEmail) {
    logger.error('Provider email not found in session storage');
    return;
  }

  const inputElement = document.getElementById('idp-discovery-username');

  if (!inputElement) {
    logger.debug('IDP discovery input element not found', { providerEmail });
    return;
  }

  inputElement.value = providerEmail;

  // Required for Okta widget to recognize the input change
  const event = new Event('input', { bubbles: true });
  inputElement.dispatchEvent(event);

  const submitButton = document.getElementById('idp-discovery-submit');
  submitButton.click();
}

function setEhrDisplayOptions(displayOptions) {
  if (displayOptions.hideNavBar) {
    sessionStorage.setItem(EHR_HIDE_NAV_BAR, 'true');
  }
  if (displayOptions.hideTopSearch) {
    sessionStorage.setItem(EHR_HIDE_TOP_SEARCH, 'true');
  }
  if (displayOptions.hidePatientBanner) {
    sessionStorage.setItem(EHR_HIDE_PATIENT_BANNER, 'true');
  }
  if (displayOptions.hidePatientSimilarity) {
    sessionStorage.setItem(EHR_HIDE_PATIENT_SIMILARITY, 'true');
  }
  if (displayOptions.hidePatientFileUpload) {
    sessionStorage.setItem(EHR_HIDE_PATIENT_FILE_UPLOAD, 'true');
  }
  if (displayOptions.hidePatientReportActions) {
    sessionStorage.setItem(EHR_HIDE_PATIENT_REPORT_ACTIONS, 'true');
  }
  if (displayOptions.hideDataDownload) {
    sessionStorage.setItem(EHR_HIDE_DATA_DOWNLOAD, 'true');
  }
}

async function PostLoginActions(email) {
  try {
    await providerAccountService.post(`/v1/ehr-launch/post-login/${encodeURIComponent(email)}`);
  } catch (error) {
    logger.warn('Error handling post login actions for EHR launch', { email, error: error.message });
  }
}

export const exportedForTesting = {
  ehrIntegrationService,
  providerAccountService,
  EHR_LAUNCH_ISSUER,
  EHR_CLIENT_ID,
  PostLoginActions,
};
